import {
  AppstoreOutlined,
  DownOutlined,
  LogoutOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import * as AllIcon from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Breadcrumb,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Popover,
  Typography,
  type MenuProps,
} from '@yimoka/antd';
import { createElement, type ComponentType, type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DocumentTitle } from '@/components/document-title';
import { LanguageSwitcher } from '@/components/language-switcher';
import type { IAuthAccountStaff } from '@/entities/auth-account.d';
import { useTranslation } from '@/locales/i18n';
import { useRealmBrand } from '@/realm-profile/profile';
import { clearAuthSession, getStaff } from '@/session/auth-session';
import { TENANT_SELECTOR_SWITCH_PATH } from '@/utils/auth-redirect';

import { useAdminLayoutStyles } from './index.style';
import {
  findAdminMenuTrail,
  findFirstAdminMenuPath,
  getAdminBreadcrumbTail,
  getAdminResourceMenuTranslationKey,
  splitAdminTopMenus,
  type AdminMenuItem,
} from './menu';
const SIDER_WIDTH = 248;
const COLLAPSED_WIDTH = 64;
const DEFAULT_AVATAR_URL = '/assets/default-avatar.png';

/** isImageIcon 判断菜单图标是否为可直接渲染的图片资源。 */
function isImageIcon(icon = '') {
  return /^(?:data:|https?:\/\/|\/)/.test(icon);
}

/** getMenuIcon 将权限菜单的图片或 Ant Design 图标名转换为可渲染节点。 */
function getMenuIcon(item: AdminMenuItem) {
  if (!item.icon) return null;
  if (isImageIcon(item.icon)) {
    return (
      <img
        alt=""
        data-api-icon={item.icon}
        height={18}
        src={item.icon}
        width={18}
      />
    );
  }
  const icon = AllIcon[item.icon as keyof typeof AllIcon];
  return icon ? createElement(icon as ComponentType) : null;
}

/** getLocalizedMenuLabel 优先使用资源空间稳定翻译键，其他菜单继续采用权限接口标题。 */
function getLocalizedMenuLabel(item: AdminMenuItem, translate: (key: string) => string) {
  const translationKey = getAdminResourceMenuTranslationKey(item);
  return translationKey ? translate(translationKey) : item.label;
}

/** toMenuItems 将 Host 菜单转换为侧栏协议；旧站只在侧栏一级分组展示图标。 */
function toMenuItems(
  items: AdminMenuItem[],
  translate: (key: string) => string,
  menuBadges: Readonly<Record<string, string>>,
  menuLabelClass: string,
  depth = 0,
): MenuProps['items'] {
  return items.map((item) => {
    const hasChildren = Boolean(item.children?.length);
    const path = getMenuTargetPath(item);
    const label = getLocalizedMenuLabel(item, translate);
    const badge = menuBadges[item.key];
    const hasBadge = Boolean(badge && badge !== '0');
    // 无角标菜单继续沿用原始文本节点，避免角标能力改变其他管理菜单的 DOM 与布局。
    const labelNode = hasBadge
      ? (
          <span className={menuLabelClass}>
            <span>{label}</span>
            <Badge count={badge} />
          </span>
      )
      : label;
    return {
      children: hasChildren ? toMenuItems(item.children ?? [], translate, menuBadges, menuLabelClass, depth + 1) : undefined,
      icon: depth === 0 ? getMenuIcon(item) : undefined,
      key: item.key,
      label: !hasChildren && path ? <Link to={path}>{labelNode}</Link> : labelNode,
    };
  });
}

/** getMenuTargetPath 返回菜单自身或首个可访问子页面的规范地址。 */
function getMenuTargetPath(item?: AdminMenuItem) {
  if (!item) return '';
  return item.children?.length ? findFirstAdminMenuPath(item.children) : (item.path ?? '');
}

/** CollapseIcon 渲染管理台侧栏收起与展开图标。 */
function CollapseIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="14"
      viewBox="0 0 14 14"
      width="14"
    >
      <path d="M7.6836 7.00041L4.79621 9.88773L5.62116 10.7127L9.3335 7.00041L5.62116 3.28809L4.79621 4.11304L7.6836 7.00041Z" fill="currentColor" />
    </svg>
  );
}

export interface AdminLayoutProps {
  browserLocation?: Pick<Location, 'assign' | 'replace'>;
  children: ReactNode | ((pageHeaderTarget: HTMLDivElement | null) => ReactNode);
  menuBadges?: Readonly<Record<string, string>>;
  menus: AdminMenuItem[];
  tenantID: string;
  tenantName?: string;
}

interface AdminSiderMenuProps {
  activeOpenKeys: string[];
  collapsed: boolean;
  menuBadges: Readonly<Record<string, string>>;
  menus: AdminMenuItem[];
  selectedKeys: string[];
}

/** 二级菜单保留用户的展开选择，仅在切换顶层菜单或激活分支时由外层 key 重建。 */
function AdminSiderMenu({
  activeOpenKeys,
  collapsed,
  menuBadges,
  menus,
  selectedKeys,
}: AdminSiderMenuProps) {
  const { t } = useTranslation();
  const { styles } = useAdminLayoutStyles();
  const [openKeys, setOpenKeys] = useState(activeOpenKeys);

  return (
    <Menu
      className={styles.siderMenu}
      data-testid="tenant-admin-menu"
      inlineCollapsed={collapsed}
      items={toMenuItems(menus, t, menuBadges, styles.menuLabel)}
      mode="inline"
      openKeys={collapsed ? [] : openKeys}
      selectedKeys={selectedKeys}
      onOpenChange={keys => setOpenKeys(keys.map(String))}
    />
  );
}

/** switchAdminTenant 退出当前租户路由并进入租户选择页。 */
function switchAdminTenant(browserLocation: Pick<Location, 'assign'>) {
  // 切换租户必须退出当前租户 basename，由外层认证路由重新建立员工上下文。
  browserLocation.assign(TENANT_SELECTOR_SWITCH_PATH);
}

/** logoutAdmin 清理双层认证状态并进入登录页。 */
function logoutAdmin(browserLocation: Pick<Location, 'replace'>) {
  // 登出先清空用户和租户员工双层凭证，再交由外层登录页重新认证。
  clearAuthSession();
  browserLocation.replace('/login');
}

/** AdminLayout 组合 Host 顶层菜单、产品菜单、侧栏、页头挂载点和账号动作。 */
// eslint-disable-next-line complexity -- Host 布局需要同时协调权限菜单、折叠态、路由跳转和页头挂载契约。
export function AdminLayout({
  browserLocation = window.location,
  children,
  menuBadges = {},
  menus,
  tenantID,
  tenantName,
}: AdminLayoutProps) {
  const { styles } = useAdminLayoutStyles();
  const { t } = useTranslation();
  const brand = useRealmBrand(t('Admin:shell.title'));
  const location = useLocation();
  const staff = getStaff<IAuthAccountStaff>();
  const [collapsed, setCollapsed] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const menuTrail = useMemo(
    () => findAdminMenuTrail(menus, location.pathname),
    [location.pathname, menus],
  );
  const { primaryMenus, productMenus } = useMemo(() => splitAdminTopMenus(menus), [menus]);
  const activeTopMenu = menuTrail[0];
  const activeProductMenu = productMenus.find(item => item.key === activeTopMenu?.key);
  const siderMenus = activeTopMenu?.children ?? [];
  const selectedKeys = menuTrail.length ? [menuTrail[menuTrail.length - 1]!.key] : [];
  const activeSiderOpenKeys = menuTrail.slice(1, -1)
    .filter(item => item.children?.length)
    .map(item => item.key);
  const siderMenuKey = `${activeTopMenu?.key ?? 'none'}:${activeSiderOpenKeys.join('/')}`;
  const firstPath = findFirstAdminMenuPath(menus);
  const staffName = staff?.name || t('Admin:shell.staffFallback');
  const resolvedTenantName = tenantName
    || staff?.tenant?.nameLocal
    || staff?.tenant?.name
    || tenantID;
  const pageTitle = menuTrail.length
    ? getLocalizedMenuLabel(menuTrail[menuTrail.length - 1]!, t)
    : undefined;
  const breadcrumbTail = getAdminBreadcrumbTail(
    location.pathname,
    menuTrail[menuTrail.length - 1]?.path,
  );
  const [pageHeaderTarget, setPageHeaderTarget] = useState<HTMLDivElement | null>(null);

  const productContent = (
    <div className={styles.productList} data-testid="tenant-admin-product-menu">
      {productMenus.map((item) => {
        const path = getMenuTargetPath(item);
        return path ? (
          <Link
            key={item.key}
            className={`${styles.productItem} ${item.key === activeProductMenu?.key ? styles.productItemSelected : ''}`}
            to={path}
            onClick={() => setProductOpen(false)}
          >
            <span className={styles.menuIcon}>{getMenuIcon(item)}</span>
            <span>{getLocalizedMenuLabel(item, t)}</span>
          </Link>
        ) : null;
      })}
    </div>
  );

  return (
    <Layout className={styles.layout} data-testid="tenant-admin-layout">
      <DocumentTitle suffix={t('Admin:shell.title')} title={pageTitle} />
      <Layout.Header className={styles.header} data-testid="tenant-admin-header">
        {firstPath ? <Link className={styles.brand} to={firstPath}>
          <img alt={brand.configured ? brand.logoAlt : t('Admin:shell.logoAlt')} className={styles.logo} src={brand.logoURL} />
          <Typography.Text strong className={styles.brandTitle}>{brand.shortTitle}</Typography.Text>
        </Link> : <div className={styles.brand}>
          <img alt={brand.configured ? brand.logoAlt : t('Admin:shell.logoAlt')} className={styles.logo} src={brand.logoURL} />
          <Typography.Text strong className={styles.brandTitle}>{brand.shortTitle}</Typography.Text>
        </div>}

        <nav aria-label={t('Admin:shell.breadcrumb')} className={styles.topNavigation}>
          {primaryMenus.map((item) => {
            const path = getMenuTargetPath(item);
            return path ? (
              <Link
                key={item.key}
                className={`${styles.topMenuItem} ${item.key === activeTopMenu?.key ? styles.topMenuItemSelected : ''}`}
                to={path}
              >
                <span className={styles.menuIcon}>{getMenuIcon(item)}</span>
                <span>{getLocalizedMenuLabel(item, t)}</span>
              </Link>
            ) : null;
          })}
          {productMenus.length ? (
            <Popover
              arrow={false}
              content={productContent}
              open={productOpen}
              placement="bottomLeft"
              trigger="click"
              onOpenChange={setProductOpen}
            >
              <button
                className={`${styles.topMenuItem} ${activeProductMenu ? styles.topMenuItemSelected : ''}`}
                data-testid="tenant-admin-product-trigger"
                type="button"
              >
                <span className={styles.menuIcon}>
                  {activeProductMenu ? getMenuIcon(activeProductMenu) : <AppstoreOutlined />}
                </span>
                <span>{activeProductMenu
                  ? getLocalizedMenuLabel(activeProductMenu, t)
                  : t('Admin:shell.productSettings')}</span>
                <DownOutlined className={productOpen ? styles.productArrowOpen : styles.productArrow} />
              </button>
            </Popover>
          ) : null}
        </nav>

        <Flex align="center" className={styles.headerActions} gap="middle">
          <LanguageSwitcher showLabel={false} />
          <Link className={styles.workspaceButton} to="/workspace">
            {t('Admin:shell.workspace')}
          </Link>
          <Dropdown
            menu={{
              items: [
                { key: 'tenant', icon: <SwapOutlined />, label: t('Admin:shell.switchTenant') },
                { key: 'logout', icon: <LogoutOutlined />, label: t('Common:logout') },
              ],
              onClick: ({ key }) => {
                if (key === 'tenant') switchAdminTenant(browserLocation);
                if (key === 'logout') logoutAdmin(browserLocation);
              },
            }}
            placement="bottomRight"
          >
            <Flex align="center" className={styles.profile} gap="small">
              <Avatar size={32} src={staff?.avatar || DEFAULT_AVATAR_URL} />
              <Flex vertical className={styles.profileText} gap={0}>
                <Typography.Text className={styles.staffName}>{staffName}</Typography.Text>
                <Typography.Text className={styles.tenantName} type="secondary">{resolvedTenantName}</Typography.Text>
              </Flex>
              <DownOutlined className={styles.profileArrow} />
            </Flex>
          </Dropdown>
        </Flex>
      </Layout.Header>

      <Layout className={styles.body}>
        {siderMenus.length ? (
          <Layout.Sider
            className={styles.sider}
            collapsed={collapsed}
            collapsedWidth={COLLAPSED_WIDTH}
            data-collapsed={collapsed}
            data-testid="tenant-admin-sider"
            theme="light"
            trigger={null}
            width={SIDER_WIDTH}
          >
            <div className={styles.siderScroll}>
              <AdminSiderMenu
                key={siderMenuKey}
                activeOpenKeys={activeSiderOpenKeys}
                collapsed={collapsed}
                menuBadges={menuBadges}
                menus={siderMenus}
                selectedKeys={selectedKeys}
              />
            </div>
            <button
              aria-label={collapsed ? t('Admin:shell.expand') : t('Admin:shell.collapse')}
              className={styles.collapseToggle}
              type="button"
              onClick={() => setCollapsed(current => !current)}
            >
              <span className={collapsed ? styles.collapseIconCollapsed : styles.collapseIcon}>
                <CollapseIcon />
              </span>
            </button>
          </Layout.Sider>
        ) : null}
        <Layout.Content
          className={styles.content}
          data-route-path={location.pathname}
          data-testid="tenant-admin-content"
        >
          <div className={styles.pageHeader} data-testid="tenant-admin-page-header">
            <Breadcrumb
              className={styles.breadcrumb}
              items={[
                { title: firstPath ? <Link to={firstPath}>{t('Admin:shell.breadcrumb')}</Link> : t('Admin:shell.breadcrumb') },
                ...menuTrail.map(item => ({
                  title: getMenuTargetPath(item)
                    ? <Link to={getMenuTargetPath(item)}>{getLocalizedMenuLabel(item, t)}</Link>
                    : getLocalizedMenuLabel(item, t),
                })),
                ...breadcrumbTail.map(title => ({ title })),
              ]}
            />
            <div ref={setPageHeaderTarget} className={styles.pageHeaderActions} data-testid="tenant-admin-page-actions" />
          </div>
          {typeof children === 'function' ? children(pageHeaderTarget) : children}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export default AdminLayout;
